import { ref, readonly, computed, ComputedRef } from 'vue';

export interface RendererError {
  id: string;
  message: string;
  source?: string;
}

// Module-level state — shared across all consumers (controls, util composables, etc.)
const listErrors = ref<RendererError[]>([]);

export function useErrors(control?: ComputedRef<any>) {
  function addError(error: Omit<RendererError, 'id'>): string {
    const existing = listErrors.value.find((e) => e.message === error.message && e.source === error.source);
    if (existing) return existing.id;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    listErrors.value.push({ ...error, id });
    return id;
  }

  function removeError(id: string): void {
    listErrors.value = listErrors.value.filter((e) => e.id !== id);
  }

  function removeErrorsBySource(source: string): void {
    listErrors.value = listErrors.value.filter((e) => e.source !== source);
  }

  function clearErrors(): void {
    listErrors.value = [];
  }

  function hasErrors(): boolean {
    return listErrors.value.length > 0;
  }

  function getErrorsBySource(source: string): RendererError[] {
    return listErrors.value.filter((e) => e.source === source);
  }

  const scopedErrors = control?.value?.path
    ? computed(() => listErrors.value.filter((e) => e.source === control?.value?.path))
    : readonly(listErrors);

  return {
    listErrors: scopedErrors,
    addError,
    removeError,
    removeErrorsBySource,
    clearErrors,
    hasErrors,
    getErrorsBySource,
  };
}
